import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import type { SupportedLanguage } from '@/i18n';
import { api } from '@/lib/api';
import { getTranslatedError } from '@/lib/error-translator';
import { useAuthStore } from '@/stores/auth';

interface UseLanguageChangeOptions {
  /** When true, shows a toast after first language selection */
  showFirstLoginToast?: boolean;
  /** Callback fired after successful language change */
  onSuccess?: () => void;
  /** Callback fired when language change fails */
  onError?: (error: Error) => void;
}

interface UseLanguageChangeReturn {
  /** Change the language - updates i18n, localStorage, and server */
  changeLanguage: (lang: SupportedLanguage) => Promise<void>;
  /** Whether the language change is in progress */
  isPending: boolean;
}

/**
 * Hook for changing the user's language preference.
 * Handles i18n, localStorage, and server persistence in one place.
 */
export function useLanguageChange(options: UseLanguageChangeOptions = {}): UseLanguageChangeReturn {
  const { showFirstLoginToast = false, onSuccess, onError } = options;
  const { i18n, t } = useTranslation();
  const { user, setUser } = useAuthStore();
  const { toast } = useToast();
  const [isPending, setIsPending] = useState(false);

  const changeLanguage = async (lang: SupportedLanguage) => {
    setIsPending(true);

    try {
      // Change language in i18next immediately for responsive UI
      await i18n.changeLanguage(lang);

      // Persist to localStorage for non-authenticated users and as fallback
      localStorage.setItem('pregoo-language', lang);

      // If logged in, persist to user profile
      if (user) {
        const response = await api.patch<{ data: typeof user }>('/users/me', {
          preferredLanguage: lang,
        });

        if (response.data) {
          setUser({ ...user, preferredLanguage: lang });

          // Show toast for first-time language selection
          if (showFirstLoginToast) {
            toast({
              title: t('common:language.firstSelectionTitle'),
              description: t('common:language.firstSelectionDescription'),
              duration: 6000,
            });
          }
        }
      }

      onSuccess?.();
    } catch (error: unknown) {
      // Language still changed locally even if server fails
      // Show user-friendly error message instead of just logging
      toast({
        title: t('common:status.error'),
        description: getTranslatedError(t, error, {
          resource: 'user',
          fallback: t('common:language.syncFailed'),
        }),
        variant: 'destructive',
      });
      onError?.(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      setIsPending(false);
    }
  };

  return {
    changeLanguage,
    isPending,
  };
}
